import * as THREE from 'three';

export type ShotLineKind = 'pistol' | 'silenced' | 'shotgun' | 'rifle';

const POOL = 16;
const _dir = new THREE.Vector3();
const _start = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

function lineMat(color: number, opacity: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
}

/** Short additive tracers from muzzle to impact. */
export class ShotLineSystem {
  private readonly live: THREE.Mesh[] = [];
  private readonly pool: THREE.Mesh[] = [];
  private readonly geo = new THREE.CylinderGeometry(1, 0.4, 1, 6, 1, true);
  private readonly mats: Record<ShotLineKind, THREE.MeshBasicMaterial> = {
    pistol: lineMat(0xfff0c0, 0.96),
    silenced: lineMat(0xd0e4f4, 0.62),
    shotgun: lineMat(0xffd890, 0.78),
    rifle: lineMat(0xffd090, 0.96),
  };

  constructor(private readonly scene: THREE.Object3D) {
    for (let i = 0; i < POOL; i++) {
      const mesh = new THREE.Mesh(this.geo, this.mats.pistol);
      mesh.userData.ignoreHit = true;
      mesh.frustumCulled = true;
      mesh.visible = false;
      this.scene.add(mesh);
      this.pool.push(mesh);
    }
  }

  spawn(from: THREE.Vector3, to: THREE.Vector3, kind: ShotLineKind): void {
    const dir = _dir.subVectors(to, from);
    const len = dir.length();
    if (len < 0.12) return;
    dir.multiplyScalar(1 / len);
    const start = _start.copy(from).addScaledVector(dir, 0.05);
    const remain = len - 0.05;
    if (remain < 0.08) return;

    const shotgun = kind === 'shotgun';
    const radius = shotgun ? 0.0065 : kind === 'rifle' ? 0.01 : 0.009;
    const mesh = this.acquire();
    mesh.material = this.mats[kind];
    mesh.visible = true;
    mesh.position.copy(start).addScaledVector(dir, remain * 0.5);
    mesh.quaternion.setFromUnitVectors(_up, dir);
    mesh.scale.set(radius, remain, radius);
    mesh.userData.life = shotgun ? 0.08 : kind === 'rifle' ? 0.075 : 0.11;
    mesh.userData.maxLife = mesh.userData.life as number;
    mesh.userData.baseRadius = radius;
    this.live.push(mesh);
  }

  update(dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const line = this.live[i]!;
      line.userData.life -= dt;
      const life = line.userData.life as number;
      const maxLife = line.userData.maxLife as number;
      const t = Math.max(0, life / maxLife);
      const radius = (line.userData.baseRadius as number) * (0.55 + 0.45 * t);
      line.scale.x = radius;
      line.scale.z = radius;
      if (life <= 0) {
        this.release(line);
        this.live.splice(i, 1);
      }
    }
  }

  private acquire(): THREE.Mesh {
    const mesh = this.pool.pop();
    if (mesh) return mesh;
    const oldest = this.live.shift();
    if (oldest) return oldest;
    const extra = new THREE.Mesh(this.geo, this.mats.pistol);
    extra.userData.ignoreHit = true;
    extra.frustumCulled = true;
    this.scene.add(extra);
    return extra;
  }

  private release(mesh: THREE.Mesh): void {
    mesh.visible = false;
    this.pool.push(mesh);
  }
}
